import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDeal } from "../lib/store";
import { Card, Tile, Computed, AddToCampaignModal, useOutreach, ComposeEmailModal, useCompose } from "../components/shared";
import { nnnApi, emailApi, researchApi } from "../lib/api";
import * as m from "../lib/model";
import { ExternalLink, Copy, Building2, Sparkles, Upload, Plus, Mail, Trash2, X, Share2, Users2 } from "lucide-react";
import { BulkSelectBar } from "../components/BulkSelectBar";
import { SendToFolkModal } from "../components/SendToFolkModal";
import { toast } from "sonner";

const CURATED = [
  { name: "Essential Properties Realty Trust", site: "https://essentialproperties.com", focus: "Service & experiential single-tenant; sale-leaseback specialist", min: 1e6, max: 50e6 },
  { name: "Realty Income", site: "https://www.realtyincome.com", focus: "Largest diversified net-lease REIT; retail, industrial, gaming", min: 1e6, max: 1e9 },
  { name: "NNN REIT (National Retail Properties)", site: "https://www.nnnreit.com", focus: "Freestanding retail single-tenant net lease", min: 1e6, max: 15e6 },
  { name: "Agree Realty", site: "https://www.agreerealty.com", focus: "Retail net lease, investment-grade tenants", min: 1e6, max: 50e6 },
  { name: "Four Corners Property Trust", site: "https://www.fcpt.com", focus: "Restaurant, auto & medical-retail net lease", min: 1e6, max: 25e6 },
  { name: "Broadstone Net Lease", site: "https://www.bnl.broadstone.com", focus: "Diversified STNL — industrial, healthcare, restaurant", min: 5e6, max: 75e6 },
  { name: "W. P. Carey", site: "https://www.wpcarey.com", focus: "Corporate sale-leaseback; industrial, warehouse, retail", min: 20e6, max: 500e6 },
  { name: "Global Net Lease", site: "https://www.globalnetlease.com", focus: "Office, industrial & retail net lease (US & Europe)", min: 5e6, max: 100e6 },
  { name: "LCN Capital Partners", site: "https://www.lcncapital.com", focus: "Corporate sale-leaseback fund; logistics & industrial", min: 10e6, max: 250e6 },
  { name: "Blue Owl / Oak Street", site: "https://www.blueowl.com", focus: "Large-scale net-lease & sale-leaseback investment fund", min: 25e6, max: 1e9 },
  { name: "Postal Realty Trust", site: "https://www.postalrealtytrust.com", focus: "USPS / postal-leased single-tenant properties", min: 0.5e6, max: 10e6 },
].map((b) => ({ ...b, id: `curated-${b.name.replace(/\W+/g, "-").toLowerCase()}`, source: "curated", email: "" }));

const fmtM = (v) => (v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(v % 1e6 ? 1 : 0)}M` : v > 0 ? `$${Math.round(v / 1e3)}K` : "—");

export default function NNNBuyers() {
  const { M, navigate } = useDeal();
  const annualRent = M.sqft * M.rentRate;
  const propValue = M.slbOn && M.slbProceeds > 0 ? M.slbProceeds : M.reVal;
  const capRate = propValue > 0 ? (annualRent / propValue) * 100 : 0;

  const [stored, setStored] = useState([]);
  const [finding, setFinding] = useState(false);
  const [emailEdits, setEmailEdits] = useState({});
  const [addForm, setAddForm] = useState(null);
  const [sel, setSel] = useState({});
  const [folkOpen, setFolkOpen] = useState(false);
  const fileRef = useRef(null);
  const compose = useCompose();
  const outreach = useOutreach();
  const addAllToCampaign = () => {
    const list = ranked.map((b) => ({ name: b.contact || "", email: emailEdits[b.id] ?? b.email ?? "", company: b.name, title: "", notes: b.focus, website: b.site }));
    outreach.start(list, "SLB Buyers");
  };

  const asContact = (b) => ({ id: b.id, name: b.contact || b.name, email: emailEdits[b.id] ?? b.email ?? "", company: b.name, website: b.site || "", focus: b.focus || "" });
  const load = () => nnnApi.list().then(setStored).catch(() => {});
  useEffect(() => { load(); }, []);

  const all = useMemo(() => [...CURATED, ...stored], [stored]);
  const ranked = all.map((b) => ({ ...b, fit: propValue > 0 && propValue >= b.min && propValue <= b.max }))
    .sort((a, b) => (b.fit === a.fit ? 0 : b.fit ? 1 : -1));
  const fitCount = ranked.filter((b) => b.fit).length;
  const selectedBuyers = ranked.filter((b) => sel[b.id]).map(asContact);
  const selCount = selectedBuyers.length;

  const profileStr = `${M.dealName || "Acquisition"}: single-tenant owner-occupied ${M.leaseType || "NNN"} property, value ${m.fmt$(propValue)}, ${M.sqft} SF, annual rent ${m.fmt$(annualRent)}, ~${capRate.toFixed(1)}% implied cap rate`;

  const summaryText = () => `NNN Sale-Leaseback Opportunity — ${M.dealName || "Acquisition"}
Single-tenant, owner-occupied property
Property value: ${m.fmt$(propValue)}
Building size: ${M.sqft.toLocaleString()} SF ($${M.rentRate}/SF/yr NNN)
Annual NNN rent: ${m.fmt$(annualRent)}
Implied cap rate: ${capRate.toFixed(2)}%
Lease: ${M.leaseType || "NNN"}, ${M.leaseYrs || 15}-yr term, ${M.rentEsc || 2}%/yr escalators
Contact: ${M.analystContact || "XYZ Ventures"}${M.analystEmail ? " · " + M.analystEmail : ""}${M.analystPhone ? " · " + M.analystPhone : ""}`;

  const copySummary = () => { navigator.clipboard?.writeText(summaryText()); toast.success("Sale-leaseback summary copied"); };

  const findMore = async () => {
    setFinding(true);
    try {
      const res = await researchApi.entities({ kind: "nnn", query: `single-tenant NNN sale-leaseback buyers for a ${m.fmt$(propValue)} owner-occupied ${M.leaseType || "NNN"} property`, location: "United States" });
      const existing = new Set(all.map((b) => b.name.toLowerCase()));
      const fresh = (res.entities || []).filter((e) => e.name && !existing.has(e.name.toLowerCase()));
      for (const e of fresh) {
        await nnnApi.create({ name: String(e.name), focus: Array.isArray(e.focus) ? e.focus.join(", ") : String(e.focus || ""), site: String(e.website || ""), min: Number(e.min) || 0, max: Number(e.max) || 0, source: "ai" });
      }
      if (fresh.length) { toast.success(`Found ${fresh.length} more NNN buyer(s) via web`); load(); }
      else toast.message("No new buyers found — the list is already comprehensive");
    } catch (e) { toast.error(e?.response?.data?.detail || "Find failed"); }
    finally { setFinding(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (!lines.length) return;
      const delim = lines[0].includes(";") ? ";" : ",";
      const hdr = lines[0].toLowerCase().split(delim).map((h) => h.trim());
      const idx = (k) => hdr.findIndex((h) => h.includes(k));
      const iName = idx("name"), iEmail = idx("email"), iFocus = idx("focus"), iSite = idx("site") >= 0 ? idx("site") : idx("website"), iMin = idx("min"), iMax = idx("max");
      const items = lines.slice(1).map((l) => {
        const c = l.split(delim).map((x) => x.trim());
        const parseM = (v) => { if (!v) return 0; const n = parseFloat(v.replace(/[^0-9.]/g, "")); return /b/i.test(v) ? n * 1e9 : /m/i.test(v) ? n * 1e6 : /k/i.test(v) ? n * 1e3 : n; };
        return { name: iName >= 0 ? c[iName] : c[0], email: iEmail >= 0 ? c[iEmail] : "", focus: iFocus >= 0 ? c[iFocus] : "", site: iSite >= 0 ? c[iSite] : "", min: iMin >= 0 ? parseM(c[iMin]) : 0, max: iMax >= 0 ? parseM(c[iMax]) : 0, source: "upload" };
      }).filter((x) => x.name);
      const res = await nnnApi.bulk(items);
      toast.success(`Imported ${res.added} buyer(s)`);
      load();
    } catch { toast.error("Could not parse CSV"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveAdd = async () => {
    if (!addForm.name) { toast.error("Name required"); return; }
    await nnnApi.create({ ...addForm, min: Number(addForm.min) || 0, max: Number(addForm.max) || 0, source: "manual" });
    toast.success("Buyer added");
    setAddForm(null);
    load();
  };

  const del = async (b) => { await nnnApi.remove(b.id); load(); };

  const openCompose = (b) => {
    const to = emailEdits[b.id] || b.email;
    const first = (b.contact || "").trim().split(" ")[0];
    compose.start({
      title: `Email ${b.contact || b.name}`,
      recipients: [{ name: b.contact || b.name, email: to }],
      subject: `NNN Sale-Leaseback Opportunity — ${M.dealName || "Single-Tenant Property"} (~${m.fmt$(propValue)})`,
      body: `Hi ${first || "there"},\n\nWe're acquiring an operating business and are offering the owner-occupied real estate as a sale-leaseback. Summary:\n\n${summaryText()}\n\nWould ${b.name} be interested in reviewing? Happy to send the full package.\n\nBest,\n${M.analystContact || "XYZ Ventures"}`,
      kind: "nnn",
      dealName: M.dealName,
      M,
      vars: [["Deal name", M.dealName || ""], ["Property value", m.fmt$(propValue)], ["Annual rent", m.fmt$(annualRent)], ["Cap rate", `${capRate.toFixed(2)}%`]],
    });
  };

  return (
    <div className="page" data-testid="page-nnn">
      <div className="page-head">
        <div className="page-title">NNN Buyers</div>
        <div className="page-sub">Match the acquisition's owner-occupied real estate to single-tenant net-lease funds & send the opportunity</div>
      </div>

      {!M.slbOn && (
        <div className="callout" style={{ marginBottom: 12 }} data-testid="nnn-slb-off">
          Sale-leaseback is currently <b>OFF</b> — figures use the entered real-estate value. Turn it on in Assumptions to model proceeds.
          <button className="btn btn-sm" style={{ marginLeft: 10 }} onClick={() => navigate("assumptions")} data-testid="nnn-goto-assumptions">Edit in Assumptions</button>
        </div>
      )}

      <Card title="Real Estate Assumption Summary" testid="nnn-re-summary"
        right={<button className="btn btn-sm" onClick={copySummary} data-testid="nnn-copy-summary"><Copy size={12} />Copy SLB summary</button>}>
        <div className="grid g4">
          <Tile label="Property Value" value={m.fmt$(propValue)} testid="nnn-propvalue" />
          <Tile label="Annual NNN Rent" value={m.fmt$(annualRent)} sub={`${M.sqft.toLocaleString()} SF · $${M.rentRate}/SF`} testid="nnn-rent" />
          <Tile label="Implied Cap Rate" value={`${capRate.toFixed(2)}%`} tone={capRate >= 6.5 ? "go" : capRate >= 5 ? "amber" : "pass"} testid="nnn-cap" />
          <Tile label="Lease Term" value={`${M.leaseYrs || 15} yr`} sub={`${M.leaseType || "NNN"} · ${M.rentEsc || 2}%/yr esc.`} testid="nnn-term" />
        </div>
      </Card>

      <Card title="Matched NNN / Net-Lease Buyers" style={{ marginTop: 12 }} testid="nnn-buyers"
        right={<div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span className="pill go">{fitCount} fit ~{fmtM(propValue || 0)}</span>
          <button className="btn btn-sm" onClick={addAllToCampaign} data-testid="nnn-campaign-add"><Share2 size={12} />Add to campaign</button>
          <button className="btn btn-sm" onClick={findMore} disabled={finding} data-testid="nnn-find-more"><Sparkles size={12} />{finding ? "Finding…" : "Find more (AI)"}</button>
          <button className="btn btn-sm" onClick={() => fileRef.current?.click()} data-testid="nnn-upload"><Upload size={12} />Upload list</button>
          <button className="btn btn-sm" onClick={() => setAddForm({ name: "", email: "", focus: "", site: "", min: "", max: "" })} data-testid="nnn-add"><Plus size={12} />Add buyer</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={onFile} data-testid="nnn-file" />
        </div>}>
        {addForm && (
          <div className="banner info" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }} data-testid="nnn-add-form">
            <input className="inp" placeholder="Fund name" style={{ minWidth: 160 }} value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="nnn-add-name" />
            <input className="inp" placeholder="Email" style={{ minWidth: 160 }} value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} data-testid="nnn-add-email" />
            <input className="inp" placeholder="Focus" style={{ minWidth: 180 }} value={addForm.focus} onChange={(e) => setAddForm({ ...addForm, focus: e.target.value })} />
            <input className="inp" placeholder="Website" style={{ minWidth: 140 }} value={addForm.site} onChange={(e) => setAddForm({ ...addForm, site: e.target.value })} />
            <input className="inp" placeholder="Min $" style={{ width: 90 }} value={addForm.min} onChange={(e) => setAddForm({ ...addForm, min: e.target.value })} />
            <input className="inp" placeholder="Max $" style={{ width: 90 }} value={addForm.max} onChange={(e) => setAddForm({ ...addForm, max: e.target.value })} />
            <button className="btn btn-primary btn-sm" onClick={saveAdd} data-testid="nnn-add-save">Save</button>
            <button className="btn btn-sm" onClick={() => setAddForm(null)}>Cancel</button>
          </div>
        )}
        <BulkSelectBar count={selCount} total={ranked.length} withEmail={selectedBuyers.filter((b) => b.email).length}
          noun="buyers" allSelected={ranked.length > 0 && selCount >= ranked.length}
          onSelectAll={() => { const n = {}; ranked.forEach((b) => { n[b.id] = true; }); setSel(n); }}
          onClear={() => setSel({})} testid="nnn-bulk">
          <button className="btn btn-sm" onClick={() => setFolkOpen(true)} disabled={selCount === 0} data-testid="nnn-folk-send"><Users2 size={12} style={{ marginRight: 4 }} />Send to folk</button>
        </BulkSelectBar>
        <div className="grid g2">
          {ranked.map((b) => (
            <div key={b.id} className={`banner ${b.fit ? "info" : ""}`} style={{ display: "flex", flexDirection: "column", gap: 6, opacity: b.fit ? 1 : 0.78 }} data-testid={`nnn-buyer-${b.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={!!sel[b.id]} onChange={(e) => setSel((s2) => ({ ...s2, [b.id]: e.target.checked }))} data-testid={`nnn-check-${b.id}`} />
                <Building2 size={14} style={{ color: "var(--text2)" }} />
                <b style={{ fontSize: 13 }}>{b.name}</b>
                {b.source !== "curated" && <span className="tag" style={{ fontSize: 9 }}>{b.source}</span>}
                {b.fit && <span className="pill go" style={{ marginLeft: "auto", fontSize: 9 }} data-testid={`nnn-fit-${b.id}`}>✓ fits</span>}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text2)" }}>{b.focus}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, flexWrap: "wrap" }}>
                <span style={{ color: "var(--text3)" }}>Typical: <b style={{ color: "var(--text2)" }}>{fmtM(b.min)}–{fmtM(b.max)}</b></span>
                <input className="inp" placeholder="contact email" style={{ flex: 1, minWidth: 130, padding: "4px 6px", fontSize: 11 }}
                  value={emailEdits[b.id] ?? b.email ?? ""} onChange={(e) => setEmailEdits({ ...emailEdits, [b.id]: e.target.value })} data-testid={`nnn-email-${b.id}`} />
                <button className="btn btn-sm" onClick={() => openCompose(b)} data-testid={`nnn-send-${b.id}`}><Mail size={11} />Email</button>
                {b.site && <a className="btn btn-sm" href={b.site} target="_blank" rel="noreferrer" data-testid={`nnn-visit-${b.id}`}><ExternalLink size={11} /></a>}
                {b.source !== "curated" && <button className="btn btn-sm" onClick={() => del(b)} data-testid={`nnn-del-${b.id}`}><Trash2 size={11} /></button>}
              </div>
            </div>
          ))}
        </div>
        <div className="page-sub" style={{ marginTop: 12, fontSize: 11 }}>Deal-size ranges are typical acquisition bands; confirm current buy-box on each fund's site. "Fits" = your property value sits inside a buyer's usual range.</div>
      </Card>

      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
      <SendToFolkModal open={folkOpen} onClose={() => setFolkOpen(false)} source="nnn" label="NNN Buyers" contacts={selectedBuyers} dealName={M.dealName || ""} />
      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts} defaultGroupName={outreach.defaultName} source="nnn" />
    </div>
  );
}
